const HEADER = ["Name", "Type", "LAT", "LON"]

function escapeCsvField(value) {
  const text = String(value ?? "")
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
}

function positionToLatLon(position, context) {
  if (!Array.isArray(position) || position.length < 2) {
    throw new Error(`${context} must contain longitude and latitude`)
  }
  if (!position.every(Number.isFinite)) {
    throw new Error(`${context} must contain only finite numeric ordinates`)
  }
  return { lat: position[1], lon: position[0] }
}

function samePosition(left, right) {
  return Array.isArray(left)
    && Array.isArray(right)
    && left.length === right.length
    && left.every((value, index) => value === right[index])
}

function polygonRingPositions(ring, context) {
  if (!Array.isArray(ring)) throw new Error(`${context} must be an array`)
  if (ring.length < 4) throw new Error(`${context} must contain at least four positions`)
  const positions = ring.map((position, index) => positionToLatLon(position, `${context} position ${index + 1}`))
  if (!samePosition(ring[0], ring.at(-1))) throw new Error(`${context} must be closed`)
  return positions.slice(0, -1)
}

function geometryPositions(geometry, context) {
  if (!geometry || typeof geometry !== "object") throw new Error(`${context} is missing geometry`)
  const mapPositions = (positions, label, minimum = 1) => {
    if (!Array.isArray(positions)) throw new Error(`${label} must be an array`)
    if (positions.length < minimum) throw new Error(`${label} must contain at least ${minimum} position${minimum === 1 ? "" : "s"}`)
    return positions.map((position, index) => positionToLatLon(position, `${label} position ${index + 1}`))
  }

  switch (geometry.type) {
    case "Point": return [positionToLatLon(geometry.coordinates, `${context} Point`)]
    case "MultiPoint": return mapPositions(geometry.coordinates, `${context} MultiPoint`)
    case "LineString": return mapPositions(geometry.coordinates, `${context} LineString`, 2)
    case "MultiLineString":
      if (!Array.isArray(geometry.coordinates) || !geometry.coordinates.length) {
        throw new Error(`${context} MultiLineString must contain at least one line`)
      }
      return geometry.coordinates.flatMap((line, index) => mapPositions(line, `${context} line ${index + 1}`, 2))
    case "Polygon":
      if (!Array.isArray(geometry.coordinates) || !geometry.coordinates.length) {
        throw new Error(`${context} Polygon must contain at least one ring`)
      }
      return geometry.coordinates.flatMap((ring, index) => polygonRingPositions(ring, `${context} ring ${index + 1}`))
    case "MultiPolygon":
      if (!Array.isArray(geometry.coordinates) || !geometry.coordinates.length) {
        throw new Error(`${context} MultiPolygon must contain at least one polygon`)
      }
      return geometry.coordinates.flatMap((polygon, polygonIndex) => {
        if (!Array.isArray(polygon) || !polygon.length) {
          throw new Error(`${context} polygon ${polygonIndex + 1} must contain at least one ring`)
        }
        return polygon.flatMap((ring, ringIndex) => polygonRingPositions(ring, `${context} polygon ${polygonIndex + 1} ring ${ringIndex + 1}`))
      })
    case "GeometryCollection":
      if (!Array.isArray(geometry.geometries) || !geometry.geometries.length) {
        throw new Error(`${context} GeometryCollection must contain at least one geometry`)
      }
      return geometry.geometries.flatMap((child, index) => geometryPositions(child, `${context} geometry ${index + 1}`))
    default: throw new Error(`${context} has unsupported geometry type: ${String(geometry.type || "unknown")}`)
  }
}

export function featureCollectionToCsv(featureCollection) {
  if (!featureCollection || featureCollection.type !== "FeatureCollection" || !Array.isArray(featureCollection.features)) {
    throw new Error("CSV export requires a GeoJSON FeatureCollection with a features array")
  }

  const rows = [HEADER]
  featureCollection.features.forEach((current, featureIndex) => {
    if (!current || current.type !== "Feature") throw new Error(`Feature ${featureIndex + 1} is invalid`)
    const name = current.properties?.name ?? ""
    const type = current.properties?.featureType ?? current.geometry?.type ?? ""
    for (const { lat, lon } of geometryPositions(current.geometry, `Feature ${featureIndex + 1}`)) {
      rows.push([name, type, lat, lon])
    }
  })

  return `${rows.map((row) => row.map(escapeCsvField).join(",")).join("\r\n")}\r\n`
}
